import { PageHandler } from 'components/ContentHandlers'
import { generateWebPageProps } from 'utils/next'
import { get as _get } from 'lodash-es'
import { setPageCacheControlHeaders } from 'utils/cache'
import { normalizeContent } from 'utils/shared/hacks'
import { ArticlesFilterProvider } from 'providers/ArticlesFilterProvider'
import {
  defaultListArticles,
  fetchArticlesCategorySection,
  fetchArticlesNavSection,
  searchCategoryDirectory,
} from 'services/contentful/helpers'
import {
  ARTICLES_CONTENT_TYPES,
  CONTENT_TYPES,
  PAGE_LIMIT_ARTICLES_DEFAULT,
} from 'services/contentful/constants'
import { useRouter } from 'next/router'
import { useMemoizedContentGetter } from 'utils/contentful'

export async function getServerSideProps(context) {
  const querySlug = _get(context, 'query.categoryname')
  const videoPage = querySlug === 'videos'

  const { categoryName, contentType, allPage, isSubcategory } = await searchCategoryDirectory(
    querySlug
  )

  if (!categoryName && !videoPage)
    return {
      notFound: true,
    }

  setPageCacheControlHeaders(context.res)

  // Fetch Nav articles section
  let navArticlesData = {}
  const sectionByTypeContent = {
    [ARTICLES_CONTENT_TYPES.ARTICLE]: 'main-list-articles-learn',
    [ARTICLES_CONTENT_TYPES.CORPORATE_BLOG]: 'main-list-articles-insights-news',
    [ARTICLES_CONTENT_TYPES.CONSUMER_BLOG]: 'main-list-articles-money',
    videos: 'main-list-articles-learn-videos',
  }

  const serverSideProps = normalizeContent(
    await generateWebPageProps(context, { forceRoute: '/learn-category' })
  )
  for (let i = 0; i < serverSideProps.props.content.fields.sections.length; i++) {
    const section = serverSideProps.props.content.fields.sections[i]
    if (section?.fields?.uiComponent == 'NavArticlesSection') {
      const data = await fetchArticlesNavSection(
        sectionByTypeContent[videoPage ? 'videos' : contentType]
      )
      navArticlesData = data?.items?.[0]
      section.fields['navArticlesData'] = navArticlesData ?? null
      section.fields['allPage'] = allPage
    }

    // Fetches the articles on the HomeHeroArticle section component which has a list of articles that is manually added.
    // Example CMS entry: https://app.contentful.com/spaces/80jeq2aeq4l1/environments/dev/entries/4xV1h1uISsiYrC5prZZfMP?focusedField=name
    if (section?.fields?.uiComponent == 'HomeHeroArticle') {
      // Fetch Main List articles section
      let mainListArticlesCategory = {}
      let data
      if (!videoPage) {
        data = await fetchArticlesCategorySection(categoryName)
      } else {
        data = await fetchArticlesCategorySection('videos')
      }
      mainListArticlesCategory = data?.items?.[0]
      section.fields['mainListArticlesCategory'] = mainListArticlesCategory ?? null
    }

    if (section?.fields?.fieldName == 'PaginatedArticlesList') {
      const pageLimit =
        section?.fields?.additionalConfiguration?.jsonContent?.pageLimit ??
        PAGE_LIMIT_ARTICLES_DEFAULT
      const skip = _get(context, 'query.page') ? (_get(context, 'query.page') - 1) * pageLimit : 0
      let defaultList
      defaultList = await defaultListArticles(
        CONTENT_TYPES.LONG_FORM_CONTENT,
        skip,
        {
          showContent: true,
          ...(allPage ? { contentType: contentType } : { primaryCategory_in: [categoryName] }),
          ...(isSubcategory && !allPage ? { secondaryCategory_contains_some: [categoryName] } : {}),
          video: videoPage,
        },
        'popular',
        pageLimit
      )

      section.fields.sectionContents.push({
        fields: {
          fieldName: 'listArticles',
          defaultList: defaultList?.items ?? [],
          total: defaultList?.total ?? 0,
          categoryName,
          contentType: CONTENT_TYPES.LONG_FORM_CONTENT,
          contentTypeArticle: contentType,
          allPage,
          isSubcategory,
        },
      })
    }
  }

  const propsPagesByTypeContent = {
    [ARTICLES_CONTENT_TYPES.ARTICLE]: {
      title: `${categoryName} Articles`,
      description: `Our ${categoryName} articles cover a variety of topics related to [${categoryName}], and help inform important decisions about your personal finances.`,
    },
    [ARTICLES_CONTENT_TYPES.CORPORATE_BLOG]: { title: `${categoryName} Blogs`, description: '' },
    [ARTICLES_CONTENT_TYPES.CONSUMER_BLOG]: {
      title: `${categoryName} Blog Posts`,
      description: `Read our latest ${categoryName} blog posts to learn more about managing personal finances and other money management topics.`,
    },
    videos: { title: 'All Videos Articles', description: '' },
  }

  if (serverSideProps?.props.content?.fields?.pageMetaTitle) {
    serverSideProps.props.content.fields.pageMetaTitle = propsPagesByTypeContent[contentType].title
    serverSideProps.props.content.fields.metaDescription =
      propsPagesByTypeContent[contentType].description
  }

  return serverSideProps
}

export default function Press({ content, isMobile }) {
  const router = useRouter()
  const categoryname = router.query.categoryname
  let { listArticles } = useMemoizedContentGetter(
    content.fields.sections.find(
      (section) => section?.fields?.fieldName == 'PaginatedArticlesList'
    ),
    ['listArticles']
  )
  return (
    <ArticlesFilterProvider
      defaultState={{
        articlesObject: { articles: listArticles.defaultList, total: listArticles.total },
        filterBy: categoryname ? categoryname : '*',
        keywordsFilter: '',
        page: router.query ? router.query.page : 1,
        titleId: 'article_story_list',
        orderBy: 'popular',
      }}
    >
      <PageHandler pageContent={content} isMobileUA={isMobile} />
    </ArticlesFilterProvider>
  )
}
